import { IngredientResult } from '@shared/api/types';
import Text from '@shared/components/content/text';
import Unit from '@shared/components/content/unit';
import { EmissionBarCell } from '@shared/components/data-table/cells/emission-bar-cell';
import { HeaderCell } from '@shared/components/data-table/cells/header-cell';
import { NumberCell } from '@shared/components/data-table/cells/number-cell';
import { formatGrams } from '@shared/lib/utils';
import { ColumnDef } from '@tanstack/react-table';

export function useProductIngredientsTableColumns(
  maxEmissions: number
): ColumnDef<IngredientResult>[] {
  return [
    {
      id: 'name',
      accessorKey: 'longDesc',
      size: 170,
      minSize: 170,
      header: ({ column }) => <HeaderCell column={column} name="Ingredient" />,
      cell: ({ getValue }) => {
        const name = getValue<string>();
        return <Text includeHover>{name}</Text>;
      },
    },
    {
      id: 'weight',
      accessorKey: 'weight',
      size: 70,
      minSize: 70,
      header: ({ column }) => (
        <HeaderCell
          column={column}
          name="Weight"
          numberColumn
          unit={<Unit variant="g" />}
        />
      ),
      cell: ({ getValue }) => {
        const weight = getValue<number>();
        return <NumberCell number={weight} formatter={formatGrams} />;
      },
    },
    {
      id: 'emissions',
      accessorKey: 'emissions',
      size: 80,
      minSize: 80,
      header: ({ column }) => (
        <HeaderCell
          column={column}
          name="Emissions"
          numberColumn
          unit={<Unit variant="kgCO2e" />}
        />
      ),
      cell: ({ getValue }) => {
        const emissions = getValue<number>();
        return <NumberCell number={emissions} className="pl-0" />;
      },
    },
    {
      id: 'bar',
      accessorKey: 'emissions',
      header: () => <div className="w-full" />,
      enableSorting: false,
      size: 130,
      cell: ({ row }) => {
        const emissions = row.original.emissions;
        const carbonIntensity = row.original.totalEmissionsPerKg;
        const emissionsCategory = row.original.emissionsCategory;

        return (
          <EmissionBarCell
            type="ingredient"
            emissions={emissions}
            maxEmissions={maxEmissions}
            carbonIntensity={carbonIntensity!}
            emissionsCategory={emissionsCategory!}
            className="w-full pr-2"
          />
        );
      },
    },
  ];
}
